/**
 * Channel Setup Configurations — Guided wizard steps for each channel type.
 *
 * Each config defines the step-by-step instructions a non-technical user
 * follows to connect their messaging platform. These are used by both
 * the mock and real ChannelsService implementations.
 */

import type { ChannelSetupConfig, ChannelSetupVariant } from "./types.ts";

export const CHANNEL_SETUP_CONFIGS: Record<string, ChannelSetupConfig> = {
  telegram: {
    type: "telegram",
    name: "Telegram",
    credentialKeys: ["botToken"],
    steps: [
      {
        title: "Create a Telegram Bot",
        description:
          'Open Telegram and search for @BotFather. Send the command /newbot and follow the prompts to create your bot. Give it a name and username.',
      },
      {
        title: "Copy your bot token",
        description:
          "BotFather will give you an API token. It looks like 123456789:ABCdefGhIJKlmnOPQRsTUVwxyz. Copy the entire token.",
        inputLabel: "Bot Token",
        inputPlaceholder: "123456789:ABCdefGhIJKlmnOPQRsTUVwxyz",
        inputType: "password",
        inputKey: "botToken",
      },
      {
        title: "Start a conversation with your bot",
        description:
          'Open Telegram, find your new bot by its username, and send it /start or any message. This is required so the bot knows where to send you automated messages and reminders.',
      },
    ],
  },

  discord: {
    type: "discord",
    name: "Discord",
    credentialKeys: ["botToken"],
    steps: [
      {
        title: "Create a Discord Application",
        description:
          'Go to discord.com/developers/applications and click "New Application". Give it a name, then go to the Bot section and click "Add Bot".',
      },
      {
        title: "Copy your bot token",
        description:
          'In the Bot section, click "Reset Token" to generate a new token. Copy it immediately — you won\'t be able to see it again.',
        inputLabel: "Bot Token",
        inputPlaceholder: "your-discord-bot-token",
        inputType: "password",
        inputKey: "botToken",
      },
      {
        title: "Invite the bot to your server",
        description:
          'Go to OAuth2 → URL Generator. Select "bot" scope, then choose the permissions you want. Copy the generated URL and open it in your browser to add the bot to your server.',
      },
    ],
  },

  slack: {
    type: "slack",
    name: "Slack",
    credentialKeys: ["botToken", "signingSecret"],
    steps: [
      {
        title: "Create a Slack App",
        description:
          'Go to api.slack.com/apps and click "Create New App". Choose "From scratch", give it a name, and select your workspace.',
      },
      {
        title: "Configure bot permissions",
        description:
          'Go to OAuth & Permissions and add these Bot Token Scopes: chat:write, channels:history, im:history, app_mentions:read. Then click "Install to Workspace".',
      },
      {
        title: "Copy your bot token",
        description:
          "After installing, copy the Bot User OAuth Token from the OAuth & Permissions page.",
        inputLabel: "Bot Token",
        inputPlaceholder: "xoxb-your-slack-bot-token",
        inputType: "password",
        inputKey: "botToken",
      },
      {
        title: "Copy your signing secret",
        description:
          "Go to Basic Information and copy the Signing Secret. This is used to verify webhook requests.",
        inputLabel: "Signing Secret",
        inputPlaceholder: "your-signing-secret",
        inputType: "password",
        inputKey: "signingSecret",
      },
    ],
  },

  whatsapp: {
    type: "whatsapp",
    name: "WhatsApp (Business)",
    credentialKeys: ["accessToken", "phoneNumberId"],
    steps: [
      {
        title: "Set up a Meta Business account",
        description:
          "Go to developers.facebook.com, create a Meta developer account, and create a new app. Select the WhatsApp product.",
      },
      {
        title: "Get your access token",
        description:
          "In the WhatsApp section of your app, go to API Setup. Copy the temporary or permanent access token.",
        inputLabel: "Access Token",
        inputPlaceholder: "your-whatsapp-access-token",
        inputType: "password",
        inputKey: "accessToken",
      },
      {
        title: "Get your phone number ID",
        description:
          "On the same API Setup page, find the Phone Number ID listed under the phone number you want to use.",
        inputLabel: "Phone Number ID",
        inputPlaceholder: "123456789012345",
        inputType: "text",
        inputKey: "phoneNumberId",
      },
    ],
  },

  "whatsapp-personal": {
    type: "whatsapp-personal",
    name: "WhatsApp (Personal)",
    credentialKeys: [],
    steps: [
      {
        title: "Link your WhatsApp account",
        description:
          "In the next step you will see a QR code. Open WhatsApp on your phone, go to Settings > Linked Devices > Link a Device, and scan the code.",
      },
      {
        title: "Keep your phone connected",
        description:
          "WhatsApp Web requires your phone to be connected to the internet. If your phone goes offline, the connection will pause until it reconnects.",
      },
    ],
  },
};

/**
 * Platforms that have multiple setup variants.
 * When a user picks one of these in the channel picker,
 * show a variant chooser before starting the wizard.
 */
export const CHANNEL_VARIANTS: Record<string, ChannelSetupVariant[]> = {
  whatsapp: [
    {
      type: "whatsapp",
      label: "Official (Business)",
      description:
        "Uses Meta's Cloud API. Requires a Meta Business account and developer setup. Best for production use.",
      badge: "Recommended",
    },
    {
      type: "whatsapp-personal",
      label: "Personal (QR Code)",
      description:
        "Links your personal WhatsApp via QR code scan. No business account needed. This is not an official integration and could be blocked by WhatsApp.",
    },
  ],
};
